/**
 * React Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree,
 * logs the error, and displays a fallback UI.
 *
 * P1 Implementation - Fix 1
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Unique identifier for this boundary (for logging) */
  boundaryName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, boundaryName = 'Unknown' } = this.props;

    // Store error info
    this.setState({ errorInfo });

    // Log the error with context
    console.error(
      `[ErrorBoundary:${boundaryName}] Caught error:`,
      error,
      '\nComponent stack:',
      errorInfo.componentStack
    );

    // Call optional error handler (for external error reporting)
    onError?.(error, errorInfo);

    // TODO: Integrate with error reporting service (Sentry, etc.)
    // errorReportingService.captureException(error, {
    //   extra: { componentStack: errorInfo.componentStack },
    //   tags: { boundary: boundaryName },
    // });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided, otherwise use default ErrorFallback
      if (fallback) {
        return fallback;
      }

      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
